import { ACOES_AUDITORIA, type EventoAuditoria } from '@mymaxx/shared';
import { Botao, CampoTexto } from '@mymaxx/ui';
import { useInfiniteQuery, useQuery } from '@tanstack/react-query';
import { ChevronDown, ChevronRight } from 'lucide-react';
import { useState } from 'react';
import { gestaoApi } from '../lib/gestao-api';

/** Consulta da trilha de auditoria, com filtros e paginação por cursor. */
export function Auditoria() {
  const [acao, setAcao] = useState('');
  const [de, setDe] = useState('');
  const [ate, setAte] = useState('');
  const [expandido, setExpandido] = useState<string | null>(null);

  const acoes = useQuery({ queryKey: ['auditoria-acoes'], queryFn: gestaoApi.acoesAuditoria });

  const eventos = useInfiniteQuery({
    queryKey: ['auditoria', acao, de, ate],
    initialPageParam: undefined as number | undefined,
    queryFn: ({ pageParam }) =>
      gestaoApi.consultarAuditoria({
        acao: acao || undefined,
        de: de ? new Date(`${de}T00:00:00`).toISOString() : undefined,
        ate: ate ? new Date(`${ate}T23:59:59`).toISOString() : undefined,
        antesDe: pageParam,
        limite: 50,
      }),
    getNextPageParam: (ultima) => ultima.proximoCursor ?? undefined,
  });

  const itens = eventos.data?.pages.flatMap((p) => p.itens) ?? [];

  return (
    <div className="space-y-6">
      <header>
        <h1 className="text-xl font-semibold">Auditoria</h1>
        <p className="text-sm text-neutral-500">
          Registro de quem fez o quê no sistema, inclusive acessos a dados de clientes (LGPD).
        </p>
      </header>

      <div className="flex flex-wrap items-end gap-3">
        <label className="block text-sm font-medium">
          Ação
          <select
            value={acao}
            onChange={(e) => setAcao(e.target.value)}
            className="mt-1 block w-56 rounded-lg border border-neutral-300 px-3 py-2 text-sm dark:border-neutral-700 dark:bg-neutral-900"
          >
            <option value="">Todas</option>
            {acoes.data?.map((a) => (
              <option key={a} value={a}>
                {ACOES_AUDITORIA[a] ?? a}
              </option>
            ))}
          </select>
        </label>
        <CampoTexto rotulo="De" type="date" value={de} onChange={(e) => setDe(e.target.value)} />
        <CampoTexto rotulo="Até" type="date" value={ate} onChange={(e) => setAte(e.target.value)} />
        {(acao || de || ate) && (
          <Botao
            variante="secundario"
            onClick={() => {
              setAcao('');
              setDe('');
              setAte('');
            }}
          >
            Limpar
          </Botao>
        )}
      </div>

      <div className="overflow-x-auto rounded-card border border-neutral-200 dark:border-neutral-800">
        <table className="w-full text-sm">
          <thead className="bg-neutral-50 text-left text-xs text-neutral-500 dark:bg-neutral-900">
            <tr>
              <th className="px-4 py-2 font-medium">Quando</th>
              <th className="px-4 py-2 font-medium">Ação</th>
              <th className="px-4 py-2 font-medium">Quem</th>
              <th className="px-4 py-2 font-medium">Sobre</th>
              <th className="px-4 py-2" />
            </tr>
          </thead>
          <tbody>
            {itens.map((e: EventoAuditoria) => (
              <ItemAuditoria
                key={e.id}
                evento={e}
                aberto={expandido === e.id}
                onAlternar={() => setExpandido(expandido === e.id ? null : e.id)}
              />
            ))}
          </tbody>
        </table>

        {eventos.isPending && (
          <p className="px-4 py-6 text-center text-sm text-neutral-500">Carregando…</p>
        )}
        {!eventos.isPending && itens.length === 0 && (
          <p className="px-4 py-6 text-center text-sm text-neutral-500">
            Nenhum evento no período.
          </p>
        )}
      </div>

      {eventos.hasNextPage && (
        <div className="flex justify-center">
          <Botao
            variante="secundario"
            onClick={() => void eventos.fetchNextPage()}
            carregando={eventos.isFetchingNextPage}
          >
            Carregar mais
          </Botao>
        </div>
      )}
    </div>
  );
}

function ItemAuditoria({
  evento,
  aberto,
  onAlternar,
}: {
  evento: EventoAuditoria;
  aberto: boolean;
  onAlternar: () => void;
}) {
  const temDetalhes = evento.detalhes && Object.keys(evento.detalhes).length > 0;
  return (
    <>
      <tr className="border-t border-neutral-200 dark:border-neutral-800">
        <td className="px-4 py-2 whitespace-nowrap text-xs text-neutral-500">
          {new Date(evento.em).toLocaleString('pt-BR')}
        </td>
        <td className="px-4 py-2">{ACOES_AUDITORIA[evento.acao] ?? evento.acao}</td>
        <td className="px-4 py-2 text-neutral-600 dark:text-neutral-400">
          {evento.usuario?.nome ?? (evento.cliente ? 'Cliente' : 'Sistema')}
        </td>
        <td className="px-4 py-2 text-xs text-neutral-500">
          {evento.cliente?.nome ?? evento.alvoTipo}
        </td>
        <td className="px-4 py-2 text-right">
          {temDetalhes && (
            <button
              type="button"
              onClick={onAlternar}
              aria-label={aberto ? 'Ocultar detalhes' : 'Ver detalhes'}
              aria-expanded={aberto}
              className="rounded p-1 text-neutral-400 hover:text-neutral-700 dark:hover:text-neutral-200"
            >
              {aberto ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
            </button>
          )}
        </td>
      </tr>
      {aberto && temDetalhes && (
        <tr className="bg-neutral-50 dark:bg-neutral-900">
          <td colSpan={5} className="px-4 py-2">
            <dl className="grid gap-x-4 gap-y-1 text-xs sm:grid-cols-2">
              {evento.ip && (
                <div className="flex gap-2">
                  <dt className="text-neutral-500">IP:</dt>
                  <dd className="font-mono">{evento.ip}</dd>
                </div>
              )}
              {Object.entries(evento.detalhes ?? {}).map(([chave, valor]) => (
                <div key={chave} className="flex gap-2">
                  <dt className="text-neutral-500">{chave}:</dt>
                  <dd className="font-mono break-all">
                    {typeof valor === 'object' ? JSON.stringify(valor) : String(valor)}
                  </dd>
                </div>
              ))}
            </dl>
          </td>
        </tr>
      )}
    </>
  );
}
